import { useEffect, useMemo, useRef, useState } from "react";
import { useCountriesAndLanguages } from "@made-people/grease-countries-and-languages";
import { useAlgoliaSearch } from "@made-people/grease-algolia";
import { getSizeChartByCountryCode } from "../../../services/centra/mappers/utils/get-sizechart-by-country-code";
import {
  SelectorDefinition,
  buildSizeFacetId,
  resolveVisibleSelectors,
  sizeSelectors,
} from "./size-selector-categories";

export type SizeFacetOptionsStatus =
  | "idle"
  | "loading"
  | "ready"
  | "empty"
  | "error";

export type UseSizeFacetOptionsResult = {
  status: SizeFacetOptionsStatus;
  activeSizeChart: string;
  optionsByKey: Record<string, string[]>;
  availableSelectors: SelectorDefinition[];
};

export const useSizeFacetOptions = (
  categoryUri: string | undefined,
  enabled: boolean,
): UseSizeFacetOptionsResult => {
  const { currentCountryCode } = useCountriesAndLanguages();
  const search = useAlgoliaSearch();
  const activeSizeChart = getSizeChartByCountryCode(currentCountryCode);

  const [status, setStatus] = useState<SizeFacetOptionsStatus>("idle");
  const [optionsByKey, setOptionsByKey] = useState<Record<string, string[]>>(
    {},
  );

  const searchRef = useRef(search);
  searchRef.current = search;
  const fetchedKeyRef = useRef<string | undefined>(undefined);

  useEffect(() => {
    if (!enabled || !categoryUri) {
      return;
    }
    const fetchKey = `${activeSizeChart}:${categoryUri}`;
    if (fetchedKeyRef.current === fetchKey) {
      return;
    }
    fetchedKeyRef.current = fetchKey;

    let cancelled = false;
    const facetIds = sizeSelectors.map((selector) =>
      buildSizeFacetId(selector.dimension, activeSizeChart),
    );

    setStatus("loading");
    searchRef
      .current({
        algoliaTypeId: "product",
        searchQuery: "",
        disjunctiveFacets: [],
        algoliaRequestOptions: {
          facets: facetIds,
          hitsPerPage: 0,
          maxValuesPerFacet: 100,
          facetFilters: [[`categories.uri:${categoryUri}`]],
        },
      })
      .then((response) => {
        if (cancelled) {
          return;
        }
        const next: Record<string, string[]> = {};
        sizeSelectors.forEach((selector) => {
          const facetId = buildSizeFacetId(selector.dimension, activeSizeChart);
          next[selector.key] = Object.keys(
            response?.facets?.[facetId] ?? {},
          ).sort((a, b) => a.localeCompare(b, undefined, { numeric: true }));
        });
        setOptionsByKey(next);
        setStatus(resolveVisibleSelectors(next).length > 0 ? "ready" : "empty");
      })
      .catch(() => {
        if (cancelled) {
          return;
        }
        fetchedKeyRef.current = undefined;
        setOptionsByKey({});
        setStatus("error");
      });

    return () => {
      cancelled = true;
    };
  }, [enabled, categoryUri, activeSizeChart]);

  const availableSelectors = useMemo(
    () => resolveVisibleSelectors(optionsByKey),
    [optionsByKey],
  );

  return { status, activeSizeChart, optionsByKey, availableSelectors };
};
